import { createContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { Trie } from '../lib/trie';
import { Place } from '@/types';
import { makeSearchKey } from '@/utils/makeSearchKey';
import { formatPlaceName } from '@/lib/utils';

export type { Place };

// Define the shape of the context data
export interface PlaceNamesContextType {
  places: Place[];
  placeNames: string[];
  isLoading: boolean;
  error: Error | null;
  trie: Trie | null;
  placeMap: Map<string, Place>;
}

// Create the context with a default value
export const PlaceNamesContext = createContext<PlaceNamesContextType | null>(null);

// Define the props for the provider component
interface PlaceNamesProviderProps {
  children: ReactNode;
}

// Create the provider component
export const PlaceNamesProvider = ({ children }: PlaceNamesProviderProps) => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPlaceNames = async () => {
      setIsLoading(true);
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/find-peers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode: 'all' }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        // Store original place data without formatting
        setPlaces(data);
        console.log(`FOR SEARCH: loaded ${data.length} places`);
      } catch (e) {
        if (e instanceof Error) {
            setError(e);
        } else {
            setError(new Error('An unknown error occurred'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlaceNames();
  }, []); // Empty dependency array ensures this runs only once on mount

  const placeNames = useMemo(() => places.map((p) => p.name), [places]);

  // Build a lookup map so any key we insert into the trie resolves to its Place.
  const placeMap = useMemo(() => {
    const map = new Map<string, Place>();

    places.forEach((place) => {
      // Key for name-based search
      const nameKey = makeSearchKey(place.name);
      map.set(nameKey, place);

      // Key for FIPS-based search (ensure string)
      if (place.place_fips !== undefined && place.place_fips !== null) {
        const fipsKey = String(place.place_fips);
        map.set(fipsKey, place);
      }
    });

    return map;
  }, [places]);

  // Build the trie from the exact same keys present in placeMap.
  const trie = useMemo(() => {
    if (placeMap.size === 0) return null;

    const t = new Trie();
    placeMap.forEach((_place, key) => {
      if (typeof key === 'string' && key.length > 0) {
        t.insert(key);
      }
    });
    return t;
  }, [placeMap]);

  const value = { places, placeNames, isLoading, error, trie, placeMap };

  return <PlaceNamesContext.Provider value={value}>{children}</PlaceNamesContext.Provider>;
}; 